import React from "react";
import { useTranslation } from "react-i18next";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { SUPPORTED_LANGUAGES } from "@/i18n/config";
import { Languages } from "lucide-react";

interface LanguageSelectorProps {
  className?: string;
  showIcon?: boolean;
  showLabel?: boolean;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ 
  className = "", 
  showIcon = true, 
  showLabel = true 
}) => {
  const { i18n, t } = useTranslation();

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
  };

  const getCurrentLanguageLabel = () => {
    const currentLang = SUPPORTED_LANGUAGES.find(lang => lang.code === i18n.language);
    return currentLang ? currentLang.nativeName : i18n.language;
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {showLabel && (
        <Label htmlFor="language-selector" className="text-sm font-medium">
          {t('settings.language_selection')}
        </Label>
      )}
      <Select value={i18n.language} onValueChange={handleLanguageChange}>
        <SelectTrigger id="language-selector" className="bg-background">
          <div className="flex items-center">
            {showIcon && <Languages className="h-4 w-4 mr-2 text-blue-500" />}
            <SelectValue placeholder={t('settings.select_language')} />
          </div>
        </SelectTrigger>
        <SelectContent>
          {SUPPORTED_LANGUAGES.map((language) => (
            <SelectItem key={language.code} value={language.code}>
              <div className="flex items-center justify-between w-full">
                <span>{language.nativeName}</span>
                <span className="text-muted-foreground ml-2 text-sm">
                  {language.name}
                </span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default LanguageSelector; 